import { useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Plus,
  Upload,
  Download,
  Trash2,
  ChevronRight,
  Loader2,
  ClipboardList,
  Activity,
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  useSROConfigs,
  useDeleteSROConfig,
  useExportSROConfig,
  useImportSROConfig,
  useSROSessionStats,
} from '@/hooks/useSRO'
import { useToast } from '@/hooks/use-toast'

export function SRODashboard() {
  const { data: configs, isLoading } = useSROConfigs()
  const { data: stats } = useSROSessionStats()
  const deleteConfig = useDeleteSROConfig()
  const exportConfig = useExportSROConfig()
  const importConfig = useImportSROConfig()
  const { toast } = useToast()
  const navigate = useNavigate()

  const [showDelete, setShowDelete] = useState<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleCreateNew = () => {
    navigate('/app/sro/create')
  }

  const handleDelete = (configId: number) => {
    deleteConfig.mutate(configId, {
      onSuccess: () => {
        toast({ title: 'Configuration deleted' })
        setShowDelete(null)
      },
      onError: () => toast({ title: 'Delete failed', variant: 'destructive' }),
    })
  }

  const handleExport = (configId: number, configName: string) => {
    exportConfig.mutate(configId, {
      onSuccess: (res) => {
        const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `sro-config-${configName.replace(/\s+/g, '-').toLowerCase()}.json`
        a.click()
        URL.revokeObjectURL(url)
        toast({ title: 'Configuration exported' })
      },
      onError: () => toast({ title: 'Export failed', variant: 'destructive' }),
    })
  }

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target?.result as string)
        if (!data.name) {
          toast({ title: 'JSON must include a "name" field', variant: 'destructive' })
          return
        }
        importConfig.mutate(data, {
          onSuccess: () => toast({ title: `Imported "${data.name}" successfully` }),
          onError: (err: any) => toast({ title: err?.response?.data?.error || 'Import failed', variant: 'destructive' }),
        })
      } catch {
        toast({ title: 'Invalid JSON file', variant: 'destructive' })
      }
    }
    reader.readAsText(file)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Service Request Orchestrator</h1>
          <p className="text-muted-foreground mt-1">Manage multiple SRO configurations</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            className="hidden"
            onChange={handleImport}
          />
          <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
            <Upload className="h-4 w-4 mr-2" />
            Import JSON
          </Button>
          <Button size="sm" onClick={handleCreateNew}>
            <Plus className="h-4 w-4 mr-2" />
            New Config
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{configs?.length ?? 0}</div>
            <p className="text-xs text-muted-foreground">Configurations</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{configs?.filter((c: any) => c.is_enabled).length ?? 0}</div>
            <p className="text-xs text-muted-foreground">Enabled</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{stats?.total_active ?? 0}</div>
            <p className="text-xs text-muted-foreground">Active Sessions</p>
          </CardContent>
        </Card>
      </div>

      {/* Config List */}
      {(!configs || configs.length === 0) ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 gap-4">
            <ClipboardList className="h-12 w-12 text-muted-foreground" />
            <h3 className="text-lg font-semibold">No Configurations</h3>
            <p className="text-muted-foreground text-center max-w-md">
              Create a new SRO configuration or import one from a JSON file.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                <Upload className="h-4 w-4 mr-2" />
                Import JSON
              </Button>
              <Button onClick={handleCreateNew}>
                <Plus className="h-4 w-4 mr-2" />
                New Config
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {configs.map((cfg: any) => (
            <Card key={cfg.id} className="hover:border-primary/50 transition-colors">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <Link to={`/app/sro/${cfg.id}`} className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="rounded-lg bg-primary/10 p-2">
                      <ClipboardList className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold truncate">{cfg.name}</h3>
                        <Badge variant={cfg.is_enabled ? 'default' : 'secondary'} className="shrink-0">
                          {cfg.is_enabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {cfg.llm_provider} &middot; Created {new Date(cfg.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </Link>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Export"
                      onClick={(e) => { e.preventDefault(); handleExport(cfg.id, cfg.name) }}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Delete"
                      onClick={(e) => { e.preventDefault(); setShowDelete(cfg.id) }}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                    <Link to={`/app/sro/${cfg.id}`}>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Sessions link */}
      <Link to="/app/sro/sessions">
        <Card className="hover:border-primary/50 transition-colors cursor-pointer">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-primary/10 p-2">
                  <Activity className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">Active Sessions</h3>
                  <p className="text-xs text-muted-foreground">Monitor and manage live conversations</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {(stats?.total_active ?? 0) > 0 && (
                  <Badge variant="secondary">{stats?.total_active}</Badge>
                )}
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </div>
            </div>
          </CardContent>
        </Card>
      </Link>

      {/* Delete Dialog */}
      <Dialog open={showDelete !== null} onOpenChange={() => setShowDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Configuration</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This will permanently delete this configuration and all its intents, departments, templates, and related data. This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDelete(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => showDelete !== null && handleDelete(showDelete)}
              disabled={deleteConfig.isPending}
            >
              {deleteConfig.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
